if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) {
          idInput.disabled = false;
        }
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;
    
      evt.preventDefault();
    
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
    
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      const isValid = await this.handleBundleProduct(evt);
      if (!isValid) return; // Stop execution if the function returns a falsy value
    
      this.errorMsg = null;
      this.setErrorMsgState();
    
      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');
    
      // Store the original button text to restore it later if needed.
      const originalButtonText = this.submitBtn.innerHTML;
      this.submitBtn.innerHTML = 'Adding...';
    
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }
    
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);
    
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
    
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
    
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }
    
        if (data.status) this.setErrorMsgState(error);
    
        if (!response.ok) throw new Error(response.status);

        // Handle bundle product child items
        await this.handleBundleChildItems(formData, data);
    
        // Success: Updating UI for successful addition to cart.
        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);
    
          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();
    
          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }
    
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];
    
        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
    
        // Success: Updated buttn text to "IN CART".
        this.submitBtn.classList.add('is-success');
        this.submitBtn.classList.add('is-added');
        this.submitBtn.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0281/6924/0651/t/12/assets/check.svg" alt="" title="" class="icon"> IN CART';
    
      } catch (error) {
        console.log(error); // eslint-disable-line
    
        // Error: Reset button state to ATC.
        this.submitBtn.innerHTML = originalButtonText;
    
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
    
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Handles adding bundle child items to cart
     * @param {FormData} formData - The form data from submission
     * @param {object} parentResponse - Response from adding parent item
     */
    async handleBundleChildItems(formData, parentResponse) {
      // Check if this is a bundle product (metaobject-based)
      const bundleVariantIds = formData.get('properties[_boldVariantIds]');
      const bundleVariantQtys = formData.get('properties[_boldVariantQtys]');
      const bundleBuilderId = formData.get('properties[_boldBuilderId]');
      const bundleProductIds = formData.get('properties[_boldProductIds]');
      const bundleVariantNames = formData.get('properties[_boldVariantNames]');
      const parentQuantity = parseInt(formData.get('quantity') || '1');

      // Check for valid metaobject bundle properties
      if (!bundleVariantIds || !bundleVariantQtys || !bundleBuilderId || 
          bundleVariantIds === 'undefined' || bundleVariantQtys === 'undefined' || 
          bundleBuilderId === 'undefined') {
        return; 
      }

      // console.log('Processing metaobject bundle child items');

      const variantIds = bundleVariantIds.split(',').filter(id => id && id !== 'undefined' && id.trim() !== '');
      const variantQtys = bundleVariantQtys.split(',').filter(qty => qty && qty !== 'undefined' && qty.trim() !== '');
      const productIds = bundleProductIds ? bundleProductIds.split(',').filter(id => id && id !== 'undefined' && id.trim() !== '') : [];
      const variantNames = bundleVariantNames ? bundleVariantNames.split(',').filter(name => name && name !== 'undefined' && name.trim() !== '') : [];

      if (variantIds.length === 0 || variantQtys.length === 0) {
        return;
      }

      // Prepare all child items for batch addition with metaobject properties
      const childItems = [];
      for (let i = 0; i < variantIds.length; i++) {
        if (variantIds[i] && variantIds[i] !== 'undefined' && variantIds[i].trim() !== '') {
          const childQuantity = parseInt(variantQtys[i] || '1') * parentQuantity;
          
          childItems.push({
            id: variantIds[i],
            quantity: childQuantity,
            properties: {
              '_boldBuilderId': bundleBuilderId,
              '_boldIsChildItem': 'true',
              '_boldVariantName': variantNames[i] || '',
              '_boldProductId': productIds[i] || '',
              '_bundleSource': 'metaobject'
            }
          });
        }
      }

      if (childItems.length === 0) {
        return;
      }

      try {
        // Try OptimizedBundleManager first (metaobject-specific optimization)
        if (window.OptimizedBundleManager && window.OptimizedBundleManager.batchAddToCart) {
          const result = await window.OptimizedBundleManager.batchAddToCart(childItems);
          if (result && result.success) {
            // console.log('Successfully added metaobject bundle via OptimizedBundleManager');
            window.hideBundleLoaderCustom(); 
            return;
          }
        }

        // Use the optimized batch add function from main.js if available
        if (typeof window.batchAddToCart === 'function') {
          await window.batchAddToCart(childItems);
        } else {
          // Fallback to batch using FormData approach
          const batchFormData = new FormData();
          
          childItems.forEach((item, index) => {
            batchFormData.append(`items[${index}][id]`, item.id);
            batchFormData.append(`items[${index}][quantity]`, item.quantity);
            
            // Add metaobject-specific properties
            Object.keys(item.properties).forEach(key => {
              batchFormData.append(`items[${index}][properties][${key}]`, item.properties[key]);
            });
          });

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: batchFormData
          });

          if (!response.ok) {
            throw new Error(`Metaobject batch add failed: ${response.status}`);
          }

          // console.log('Successfully added metaobject bundle via batch FormData');
        }

        window.hideBundleLoaderCustom(); 

      } catch (error) {
        console.warn('Metaobject batch add failed, falling back to individual requests:', error);
        
        // Check if this is a validation error (400) which indicates missing bundle options
        if (error.message && error.message.includes('400')) {
          console.error('Bundle validation failed: Please ensure all required bundle options are selected before adding to cart');
          
          // Show user-friendly error message
          const errorElement = document.querySelector('.bundle-error-message');
          if (errorElement) {
            errorElement.textContent = 'Please select all required bundle options before adding to cart';
            errorElement.style.display = 'block';
            setTimeout(() => {
              errorElement.style.display = 'none';
            }, 5000);
          }
          
          window.hideBundleLoaderCustom();
          return; // Don't attempt fallback for validation errors
        }
        
        // Fallback to individual requests if batch fails (but not for validation errors)
        for (const item of childItems) {
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(item)
            });
            
            if (!response.ok && response.status === 400) {
              console.warn(`Validation error for item ${item.id}: Bundle options may be missing`);
              break; // Stop trying other items if validation fails
            }
          } catch (itemError) {
            console.warn(`Failed to add metaobject child item ${item.id}:`, itemError);
            // Continue with other items even if one fails
          }
        }
        
        window.hideBundleLoaderCustom(); 
      }
    }

    async handleBundleProduct(evt) {
      const targetElement = evt.target;
      const form = targetElement.closest('form');
      
      // Check if this is a bundle product by looking for bundle options
      const bundleOptions = form.querySelector('.bold_option_set');
      if (bundleOptions) {
        // console.log('Bundle product detected, validating options...');
        
        const selects = bundleOptions.querySelectorAll('select.bundle_option_select');
        // console.log('Found bundle selects:', selects.length);
        
        const allSelected = Array.from(selects).every(select => {
          const isSelected = select.value.trim() !== '';
          const attrName = select.getAttribute('attr-name') || 'Unknown';
          // console.log(`Select ${attrName}: "${select.value}" (selected: ${isSelected})`);
          return isSelected;
        });

        // Check if we're on a product page (main product) vs collection page
        const isProductPage = document.querySelector('.cc-main-product.product-main');
        
        if (!allSelected) {
          // console.log('Not all bundle options selected, preventing submission');
          
          // Only show the alert div error for collection pages, not product pages
          if (!isProductPage) {
            const errorWrapper = bundleOptions;
            const existingError = errorWrapper?.querySelector('.js-form-error');
            if (existingError) {
              existingError.remove();
            }
            
            const errorDiv = document.createElement('div');
            errorDiv.className = 'alert mb-8 bg-error-bg text-error-text js-form-error text-start';
            errorDiv.setAttribute('role', 'alert');
            errorDiv.textContent = 'You need to choose options for your item.';
            
            errorWrapper.appendChild(errorDiv);
          }
          // For product pages, the validation is handled by product-bundle-choose-modal.js
          
          return false; 
        }
        
        // console.log('All bundle options selected, proceeding...');
        return true; 
      }
      
      // console.log('Not a bundle product, proceeding...');
      return true;
    }
    

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          if(err == 'Required parameter missing or invalid: id') {
            this.errorMsg.insertAdjacentText('beforeend', 'You need to choose options for your item.');
          }else {
            this.errorMsg.insertAdjacentText('beforeend', err);
          }
         
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}